import type {
  ConfiguratorStepType,
  ConfiguratorStoreState,
  SerializedConfigurationState,
  SerializedOperation,
  SerializedStep,
} from '@ui/features/configurator/types';

import type { ConfiguratorResourceOptionMeta } from '../../data/field-definitions/resource';
import { ConfiguratorVendorOptionMeta } from '../../data/field-definitions/vendor';
import { FIELD_IDS, STEP_KEYS } from '../../data/fixtures/cold-headed-fastener';
import { calculateOperationSequenceNumber } from '../helpers/index-helpers';
import { blueprintStepMap } from './blueprint-helpers';
import {
  ensureDescriptionField,
  ensureMaterialField,
  ensureNumberField,
  ensureSingleChoiceField,
  ensureTextField,
  getFieldByStepId,
  getNumberFieldValue,
  getOnlyFieldByStepKey,
  getTextareaFieldValue,
} from './field-helpers';

export function serializeConfigurationState(
  state: ConfiguratorStoreState,
): SerializedConfigurationState {
  const quantityField = ensureNumberField(
    getOnlyFieldByStepKey(
      STEP_KEYS.base_fields,
      FIELD_IDS.quantity,
      state.fields,
      state.blueprint,
    ),
  );

  const customerPartIdField = ensureTextField(
    getOnlyFieldByStepKey(
      STEP_KEYS.base_fields,
      FIELD_IDS.customer_part_id,
      state.fields,
      state.blueprint,
    ),
  );

  const productCodeField = ensureSingleChoiceField(
    getOnlyFieldByStepKey(
      STEP_KEYS.base_fields,
      FIELD_IDS.product_code,
      state.fields,
      state.blueprint,
    ),
  );

  const descriptionField = ensureDescriptionField(
    getOnlyFieldByStepKey(
      STEP_KEYS.description,
      FIELD_IDS.description,
      state.fields,
      state.blueprint,
    ),
  );

  const costField = getOnlyFieldByStepKey(
    STEP_KEYS.computed_fields,
    FIELD_IDS.cost,
    state.fields,
    state.blueprint,
  );

  const materialField = ensureMaterialField(
    getOnlyFieldByStepKey(
      STEP_KEYS.material,
      FIELD_IDS.material,
      state.fields,
      state.blueprint,
    ),
  );

  const volumeCalculations = serializeStep(state, 'volume_calculations');

  if (!materialField.value?.meta?.partId) {
    throw new Error(`Missing material partId for serialization`);
  }

  if (!state.costs?.output) {
    throw new Error(`Missing costs output for serialization`);
  }

  return {
    /**
     * @NOTE(shawk): it seems useful to pull some of our prerequisites out into
     * top-level properties in the serialized data, but I'd rather not duplicate
     * that data (it'll also be serialized as part of the `prerequisites` key
     * below).
     */
    quantity: quantityField.value ?? 0,
    customerPartId: customerPartIdField.value ?? '',
    productCode: productCodeField.value?.value ?? 'NA',
    materialPartId: materialField.value?.meta?.partId,
    description: descriptionField.value?.value ?? '',
    unitCost: getNumberFieldValue(costField, 0),
    operations: serializeOperations(state),
    volumeCalculations,
    costsOutput: state.costs.output,
  };
}

function serializeOperations(
  state: ConfiguratorStoreState,
): SerializedOperation[] {
  return state.blueprint.operations.map(
    (operation, index): SerializedOperation => {
      const notesField = getFieldByStepId(
        operation.stepId,
        FIELD_IDS.notes,
        state.fields,
      );

      const sequenceNumber = calculateOperationSequenceNumber(index);
      const operationFullName = `${sequenceNumber} - ${operation.name}`;

      if (!state.costs) {
        throw new Error('Costs have not been calculated');
      }

      if (!state.costs.input[index]) {
        throw new Error(`Operation "${operationFullName}" has no costs input`);
      }

      const costsInput = state.costs.input[index];
      const snapOperation = state.costs.operations[index];
      let resourceId = snapOperation.resourceId;
      let vendorId: string | undefined;

      if (operation.operationType === 'internal') {
        const resourceField =
          ensureSingleChoiceField<ConfiguratorResourceOptionMeta>(
            getFieldByStepId(
              operation.stepId,
              FIELD_IDS.resource,
              state.fields,
            ),
          );

        if (!resourceField.value?.meta?.resourceId) {
          throw new Error(`Operation "${operationFullName}" has no resourceId`);
        }
        resourceId = resourceField.value.meta.resourceId;
      }

      if (resourceId === 'OSP') {
        const vendorField =
          ensureSingleChoiceField<ConfiguratorVendorOptionMeta>(
            getFieldByStepId(operation.stepId, FIELD_IDS.vendor, state.fields),
          );

        if (!vendorField.value?.meta?.id) {
          throw new Error(`Operation "${operationFullName}" has no vendor ID`);
        }
        vendorId = vendorField.value.meta.id;
      }

      return {
        ...snapOperation,
        name: operation.name,
        documents: operation.documents?.map((document) => document.id) ?? [],
        sequenceNum: sequenceNumber,
        resourceId,
        description: operation.description,
        notes: getTextareaFieldValue(notesField, undefined),
        fields: operation.fieldKeys.map((fieldId) => {
          const field = state.fields[fieldId];

          return {
            name: field.name,
            description: field.description,
            inputType: field.inputType,
            value: field.value,
          };
        }),
        costsInput,
        vendorId,
      };
    },
  );
}

function serializeStep(
  state: ConfiguratorStoreState,
  stepType: ConfiguratorStepType,
): SerializedStep {
  const steps = state.blueprint[blueprintStepMap[stepType]];

  return Object.fromEntries(
    steps.map((step) => {
      return [
        step.stepId,
        {
          name: step.name,
          stepId: step.stepId,
          description: step.description,
          fields: Object.fromEntries(
            step.fieldKeys.map((fieldId) => {
              const field = state.fields[fieldId];

              return [
                field.fieldId,
                {
                  name: field.name,
                  description: field.description,
                  inputType: field.inputType,
                  value: field.value,
                },
              ];
            }),
          ),
        },
      ];
    }),
  );
}
